import React, {useEffect, useRef, useState, useCallback, useMemo} from "react";
import MaterialTable, {MTableAction, MTableToolbar} from 'material-table';
import Paper from '@mui/material/Paper';
import './TableCustom.scss';

const TableCustom = ({
    data,
    initialPage,
    parentRef,
    verticalAlign,
    ...props
}) => {
    const toolbarRef = useRef();
    const containerRef = useRef();
    const [pageLengthOptions, setPageLengthOptions] = useState([10, 20]);

    const setAttr = useCallback((reference, querySelector, attr, attrVal, all = false) => {
        if (!reference.current) return;
        let selectField;

        if (all) {
            selectField = reference.current.querySelectorAll(querySelector);
            selectField.forEach(field => field.setAttribute(attr, attrVal));
        } else {
            selectField = reference.current.querySelector(querySelector);
            selectField && selectField.setAttribute(attr, attrVal);
        }
    }, []);

    useEffect(() => {
        // Add aria-labels to columns names
        const colNamesList = containerRef?.current.querySelectorAll('.MuiTableSortLabel-root');
        colNamesList.forEach(col => {
            col.setAttribute('aria-label', `${col.innerText.replace('arrow_downward', '')}. Sortuj kolumnę`)
        });

        // Add aria-label to filter fields
        const presentFieldsAriaLabeld = containerRef?.current.querySelectorAll('.MuiInputBase-input.MuiInput-input.MuiInputBase-inputAdornedStart.MuiInputBase-inputTypeSearch.MuiInput-inputTypeSearch');
        presentFieldsAriaLabeld.forEach(input => {
            const newName = input.getAttribute('aria-label').replace('filter data by', 'Filtruj kolumnę po');
            input.setAttribute('aria-label', `${newName}. Po wpisaniu każdego znaku, zawartość tabeli dynamicznie się zmieni.`);
        });

        // Add aria-label to search field and search clear btn
        setAttr(toolbarRef, '.MuiInputBase-input', 'aria-label', 'Szukaj. Po wpisaniu każdego znaku, zawartość tabeli dynamicznie się zmieni.');
        setAttr(toolbarRef, '.MuiIconButton-root', 'aria-label', 'Wyczyść pole szukaj, tabela wyświetli wszystkie wyniki.');

        if (pageLengthOptions.length > 1) {
            // Add id and label to rows select
            setAttr(containerRef, '.MuiSelect-nativeInput', 'id', 'rowSelectId');
            setAttr(containerRef, '.MuiSelect-nativeInput', 'aria-describedby', 'rowSelectedLabelId');
            setAttr(containerRef, '.MuiInputBase-root.MuiTablePagination-input.MuiTablePagination-selectRoot', 'aria-label', 'Rozwiń aby wybrać liczbę wyświetlonych wierszy na stronie');
        }
    }, [data, pageLengthOptions, setAttr]);

    useEffect(() => {
        const dataLength = data.length;

        if (dataLength > 20) {
            setPageLengthOptions([10, 20, 50]);
        }
        if (dataLength > 10 && dataLength <= 20) {
            setPageLengthOptions([10, 20]);
        }
        if (dataLength <= 10) {
            setPageLengthOptions([]);
        }
    }, [data]);

    const components = useMemo(() => ({
        Toolbar: props => (
            <div ref={toolbarRef}>
                <MTableToolbar {...props} />
            </div>
        ),
        Container: props => (
            <div ref={containerRef}>
                <Paper variant='outlined' {...props} />
                <label htmlFor='rowSelectId' className='sr-only' id='rowSelectedLabelId'>Liczba wyświetlanych wierszy</label>
            </div>
        ),
        Action: props => (
            <div className='table-custom__action'>
                <MTableAction {...props} />
            </div>
        ),
    }), []);

    return (
        <div className='table-custom'>
            <MaterialTable
                data={data}
                components={components}
                options={{
                    filtering: true,
                    showTitle: false,
                    sorting: true,
                    paginationType: 'stepped',
                    pageSize: 10,
                    pageSizeOptions: pageLengthOptions,
                    headerStyle: {backgroundColor: '#0D47A1', color: 'white'},
                    initialPage: 0,
                    draggable: false,
                    align: 'left',
                }}
                localization={{
                    pagination: {
                        firstAriaLabel: 'Pierwsza strona',
                        firstTooltip: 'Pierwsza strona',
                        previousAriaLabel: 'Poprzednia strona',
                        previousTooltip: 'Poprzednia strona',
                        nextAriaLabel: 'Następna strona',
                        nextTooltip: 'Następna strona',
                        lastAriaLabel: 'Ostatnia strona',
                        lastTooltip: 'Ostatnia strona',
                        labelRowsPerPage: 'Rzędów na stronę',
                        labelRowsSelect: 'wierszy'

                    },
                    toolbar: {
                        searchTooltip: 'Szukaj',
                        searchPlaceholder: 'Szukaj',

                    },
                    header: {
                        actions: 'Akcje'
                    },
                    body: {
                        emptyDataSourceMessage: 'Brak wyników',
                        filterRow: {
                            filterTooltip: 'Filtr'
                        }
                    }
                }}
                {...props}
            />
        </div>
    )
};

export default React.memo(TableCustom);
